// Page-level theme editor. Shown in the property panel when the page
// itself is selected. Lets the author switch a built-in preset, edit the
// palette, typography, and layout — Shopify/Wix-style.
//
// All edits patch into PageModel.theme; resolveTheme() in theme.ts merges
// the sparse overrides with the active preset before applying CSS vars.

import { useState } from 'react'
import Modal from '../../../components/common/Modal'
import { AssetPickerField } from './AssetPicker'
import {
  THEME_PRESETS,
  CONTENT_WIDTH_PX,
  DEFAULT_PRESET_ID,
  FONT_STACKS,
  GOOGLE_FONT_FAMILY,
  resolveDarkPalette,
  resolveTheme,
  smartMergePreset,
} from './theme'
import ProjectThemePanel, { useProjectThemes, useThemeRouting, type ThemeScope } from './ProjectThemePanel'
import type {
  Align,
  Background,
  ColorScheme,
  ContentWidthPreset,
  DarkModeConfig,
  FontFamilyPreset,
  PageLayoutPreset,
  PageTheme,
  PalettePreset,
  TypographyPreset,
} from './types'
import { DraftInput, OptionSelect, numOrUndef, parseWeightList, textOrUndef } from './settingFields'

interface Props {
  theme: PageTheme | undefined
  project: string
  onChange: (next: PageTheme) => void
  /** Bind / switch / detach a PROJECT theme — the page's theme AND its
   *  colour schemes change together (schemes `null` = detach). Without it
   *  the project-theme panel is hidden (e.g. a non-page host). */
  onBindTheme?: (theme: PageTheme, schemes: ColorScheme[] | null) => void
}

const FIELD_LABEL: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  display: 'block',
}

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.9125rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text)',
  outline: 'none',
}

const SECTION: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 10,
  paddingBottom: 14, borderBottom: '1px solid var(--color-border)',
}

const ROW: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'center',
}

export default function ThemeEditor({ theme, project, onChange: onChangeRaw, onBindTheme }: Props) {
  // A page bound to a project theme routes every edit below either to the
  // THEME (every bound page follows) or to this page's `theme.local`.
  const themes = useProjectThemes(onBindTheme ? project : '')
  const [scope, setScope] = useState<ThemeScope>('theme')
  const boundDoc = theme?.ref ? themes.overview?.themes.find((t) => t.id === theme.ref) : undefined
  const routing = useThemeRouting(project, theme, boundDoc, scope, themes.patchDoc, themes.reload)
  const onChange = (next: PageTheme) => onChangeRaw(routing.route(next))
  const resolved = resolveTheme(theme)
  const presetId = theme?.preset || DEFAULT_PRESET_ID

  // When the user picks a preset, hold the request here until they
  // confirm whether to keep customizations (smart merge) or replace
  // every setting with the preset defaults. Skip the prompt and apply
  // immediately when there are no customizations to lose (i.e. the
  // current theme is exactly the active preset).
  const [pendingPresetId, setPendingPresetId] = useState<string | null>(null)
  const pendingPreset = pendingPresetId
    ? THEME_PRESETS.find(p => p.id === pendingPresetId) ?? null
    : null

  const setPreset = (id: string) => {
    if (id === presetId && !hasCustomizations(theme)) return
    if (!hasCustomizations(theme)) {
      // No customizations to preserve — just apply directly.
      onChange(withDarkMode(smartMergePreset(theme, id), theme))
      return
    }
    setPendingPresetId(id)
  }

  const applyPendingSmart = () => {
    if (!pendingPresetId) return
    onChange(withDarkMode(smartMergePreset(theme, pendingPresetId), theme))
    setPendingPresetId(null)
  }

  const applyPendingReplaceAll = () => {
    if (!pendingPresetId) return
    // Full overwrite: drop every customization. The new theme is the
    // preset's full theme with no overrides — `resolveTheme()` will
    // hydrate the rest from the preset's declared values.
    const next = THEME_PRESETS.find(p => p.id === pendingPresetId)?.theme
    if (next) onChange({ ...next })
    setPendingPresetId(null)
  }

  const patchPalette = (patch: Partial<PalettePreset>) => {
    onChange({ ...(theme || {}), palette: { ...(theme?.palette || {}), ...patch } })
  }
  const patchTypography = (patch: Partial<TypographyPreset>) => {
    onChange({ ...(theme || {}), typography: { ...(theme?.typography || {}), ...patch } })
  }
  const patchLayout = (patch: Partial<PageLayoutPreset>) => {
    onChange({ ...(theme || {}), layout: { ...(theme?.layout || {}), ...patch } })
  }
  const patchDarkMode = (patch: Partial<DarkModeConfig>) => {
    onChange({ ...(theme || {}), darkMode: { ...(theme?.darkMode || {}), ...patch } })
  }
  const patchDarkPalette = (patch: Partial<PalettePreset>) => {
    patchDarkMode({ palette: { ...(theme?.darkMode?.palette || {}), ...patch } })
  }

  // The SAVED (sparse) overrides, as opposed to `resolved` (preset + runtime
  // defaults filled in). Fields whose unset state matters read these and
  // show the resolved value as the placeholder.
  const rawType = theme?.typography || {}
  const rawLayout = theme?.layout || {}
  const darkMode = theme?.darkMode || {}
  // Placeholders for the sparse dark palette: what the runtime would derive
  // if the author set nothing. Forced to 'auto' so they show even while the
  // mode is still off.
  const darkDerived = resolveDarkPalette({ ...(theme || {}), darkMode: { ...darkMode, mode: 'auto' } })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {onBindTheme && (
        <ProjectThemePanel
          project={project}
          theme={theme}
          overview={themes.overview}
          error={themes.error}
          scope={scope}
          setScope={setScope}
          status={routing.status}
          reload={themes.reload}
          onBind={onBindTheme}
        />
      )}
      {/* ── Presets ── */}
      <section style={SECTION}>
        <div style={FIELD_LABEL}>Theme preset</div>
        <p style={{ margin: '0 0 8px', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          Switching presets keeps any color or font you've already customized.
          Use "Replace all" in the prompt to discard customizations and start
          fresh from a preset's defaults.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {THEME_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              title={p.description}
              style={{
                textAlign: 'left',
                padding: '8px 10px',
                border: `1.5px solid ${presetId === p.id ? 'var(--color-accent)' : 'var(--color-border)'}`,
                borderRadius: 8,
                background: presetId === p.id ? 'rgba(76,141,255,0.08)' : 'var(--color-bg)',
                cursor: 'pointer',
                fontSize: '0.8625rem',
                color: 'var(--color-text)',
              }}
            >
              <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                {[
                  p.theme.palette?.primary,
                  p.theme.palette?.accent,
                  p.theme.palette?.background,
                  p.theme.palette?.text,
                ].map((c, i) => (
                  <span
                    key={i}
                    style={{
                      width: 14, height: 14, borderRadius: 4,
                      background: c || '#ccc',
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}
                  />
                ))}
              </div>
              <div style={{ fontWeight: 600 }}>{p.label}</div>
            </button>
          ))}
        </div>

        {/* "Replace all" affordance for the active preset. Lets the
            user wipe every override on the current page and re-load the
            preset's pristine palette/typography/layout — useful when
            previous edits accumulated and the page no longer matches
            the chosen look. Only enabled when there's something to
            reset (otherwise it's a noop). */}
        {hasCustomizations(theme) && (
          <button
            type="button"
            onClick={() => setPendingPresetId(presetId)}
            style={{
              marginTop: 4,
              padding: '6px 10px', fontSize: '0.8125rem',
              background: 'transparent',
              border: '1px dashed var(--color-border)', borderRadius: 6,
              color: 'var(--color-text-secondary)', cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
            title="Discard every theme customization and reload the preset's defaults"
          >
            Reset to {THEME_PRESETS.find(p => p.id === presetId)?.label || 'preset'} defaults…
          </button>
        )}

      </section>

      {/* Confirmation modal — surfaces above the property panel so the
          user can't miss the warning. Rendered as a sibling of the
          preset section (not inside it) so the modal overlay covers
          the whole app, not just the panel. */}
      <PresetSwitchModal
        preset={pendingPreset}
        isReset={!!pendingPreset && pendingPreset.id === presetId}
        onKeep={applyPendingSmart}
        onReplace={applyPendingReplaceAll}
        onCancel={() => setPendingPresetId(null)}
      />

      {/* ── Palette ──
          Tokens are grouped by purpose so the editor reads as a design
          system rather than a flat color soup. Each group is a
          `<details>` that defaults to closed for the rarely-edited
          extended tokens (text hierarchy, surface stack, semantic) —
          the brand group is open so the casual case (pick brand colors,
          let the rest auto-derive) is one click. */}
      <section style={SECTION}>
        <div style={FIELD_LABEL}>Color palette</div>
        <p style={{ margin: '0 0 8px', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          Brand colors drive the rest — text, surfaces, badges, and semantic
          states all auto-derive from the chosen palette. Open the other
          groups to fine-tune individual tokens.
        </p>

        {/* Brand — always open */}
        <PaletteGroup label="Brand" defaultOpen>
          <ColorRow label="Primary" value={resolved.palette.primary}
            onChange={(c) => patchPalette({ primary: c })} />
          {/* Hover/derived tokens: clearing the text field un-sets the
              override (undefined) so the runtime re-derives it. */}
          <ColorRow label="Primary hover" value={resolved.palette.primaryHover}
            onChange={(c) => patchPalette({ primaryHover: c || undefined })} />
          <ColorRow label="On primary" value={resolved.palette.onPrimary}
            onChange={(c) => patchPalette({ onPrimary: c })} />
          <ColorRow label="Accent" value={resolved.palette.accent}
            onChange={(c) => patchPalette({ accent: c })} />
          <ColorRow label="Accent hover" value={resolved.palette.accentHover}
            onChange={(c) => patchPalette({ accentHover: c || undefined })} />
          <ColorRow label="On accent" value={resolved.palette.onAccent}
            onChange={(c) => patchPalette({ onAccent: c })} />
        </PaletteGroup>

        {/* Text hierarchy */}
        <PaletteGroup label="Text hierarchy">
          <ColorRow label="Heading" value={resolved.palette.heading}
            onChange={(c) => patchPalette({ heading: c })} />
          <ColorRow label="Body" value={resolved.palette.text}
            onChange={(c) => patchPalette({ text: c })} />
          <ColorRow label="Muted" value={resolved.palette.muted}
            onChange={(c) => patchPalette({ muted: c })} />
          <ColorRow label="Subtle" value={resolved.palette.subtle}
            onChange={(c) => patchPalette({ subtle: c })} />
          <ColorRow label="Link" value={resolved.palette.link}
            onChange={(c) => patchPalette({ link: c })} />
          <ColorRow label="Link hover" value={resolved.palette.linkHover}
            onChange={(c) => patchPalette({ linkHover: c || undefined })} />
          <ColorRow label="Inverted" value={resolved.palette.inverted}
            onChange={(c) => patchPalette({ inverted: c })} />
          <ColorRow label="On surface" value={resolved.palette.onSurface}
            onChange={(c) => patchPalette({ onSurface: c || undefined })} />
          <ColorRow label="On surface alt" value={resolved.palette.onSurfaceAlt}
            onChange={(c) => patchPalette({ onSurfaceAlt: c || undefined })} />
        </PaletteGroup>

        {/* Surface stack */}
        <PaletteGroup label="Surfaces">
          <ColorRow label="Background" value={resolved.palette.background}
            onChange={(c) => patchPalette({ background: c })} />
          <ColorRow label="Surface" value={resolved.palette.surface}
            onChange={(c) => patchPalette({ surface: c })} />
          <ColorRow label="Surface alt" value={resolved.palette.surfaceAlt}
            onChange={(c) => patchPalette({ surfaceAlt: c })} />
          <ColorRow label="Surface muted" value={resolved.palette.surfaceMuted}
            onChange={(c) => patchPalette({ surfaceMuted: c })} />
          <ColorRow label="Elevated" value={resolved.palette.elevated}
            onChange={(c) => patchPalette({ elevated: c })} />
          <ColorRow label="Border" value={resolved.palette.border}
            onChange={(c) => patchPalette({ border: c })} />
          <ColorRow label="Border strong" value={resolved.palette.borderStrong}
            onChange={(c) => patchPalette({ borderStrong: c })} />
        </PaletteGroup>

        {/* Semantic state colors */}
        <PaletteGroup label="Semantic states">
          <ColorRow label="Info" value={resolved.palette.info}
            onChange={(c) => patchPalette({ info: c })} />
          <ColorRow label="Info bg" value={resolved.palette.infoBg}
            onChange={(c) => patchPalette({ infoBg: c || undefined })} />
          <ColorRow label="Success" value={resolved.palette.success}
            onChange={(c) => patchPalette({ success: c })} />
          <ColorRow label="Success bg" value={resolved.palette.successBg}
            onChange={(c) => patchPalette({ successBg: c || undefined })} />
          <ColorRow label="Warning" value={resolved.palette.warning}
            onChange={(c) => patchPalette({ warning: c })} />
          <ColorRow label="Warning bg" value={resolved.palette.warningBg}
            onChange={(c) => patchPalette({ warningBg: c || undefined })} />
          <ColorRow label="Error" value={resolved.palette.error}
            onChange={(c) => patchPalette({ error: c })} />
          <ColorRow label="Error bg" value={resolved.palette.errorBg}
            onChange={(c) => patchPalette({ errorBg: c || undefined })} />
        </PaletteGroup>

        {/* Live token preview — small swatch grid showing the resolved
            tokens so the user can sanity-check contrast at a glance. */}
        <ThemePreview palette={resolved.palette} />
      </section>

      {/* ── Typography ── */}
      <section style={SECTION}>
        <div style={FIELD_LABEL}>Typography</div>
        {/* Each family = a curated preset + an optional ANY-Google-family
            override. When the custom name is set the runtime renders it and
            the preset only supplies the fallback stack — the picker says
            so instead of implying the preset is what visitors see. */}
        <div>
          <label style={miniLabel}>Body font</label>
          <FontPicker
            value={resolved.typography.fontFamily}
            onChange={(f) => patchTypography({ fontFamily: f })}
            overriddenBy={rawType.customFontFamily}
          />
          <CustomFamilyInput
            label="Custom body font (Google family)"
            value={rawType.customFontFamily}
            presetFamily={GOOGLE_FONT_FAMILY[resolved.typography.fontFamily]}
            onCommit={(v) => patchTypography({ customFontFamily: v })}
          />
        </div>
        <div>
          <label style={miniLabel}>Heading font</label>
          <FontPicker
            value={resolved.typography.headingFamily}
            onChange={(f) => patchTypography({ headingFamily: f })}
            overriddenBy={rawType.customHeadingFamily}
          />
          <CustomFamilyInput
            label="Custom heading font (Google family)"
            value={rawType.customHeadingFamily}
            presetFamily={GOOGLE_FONT_FAMILY[resolved.typography.headingFamily]}
            onCommit={(v) => patchTypography({ customHeadingFamily: v })}
          />
        </div>
        <div>
          <label style={miniLabel}>Font weights to load</label>
          {/* Draft string committed on blur — splitting on every keystroke
              would turn "60" (on the way to "600") into a webfont request. */}
          <DraftInput
            value={(rawType.customFontWeights || []).join(', ')}
            onCommit={(raw) => patchTypography({ customFontWeights: parseWeightList(raw) })}
            placeholder="400, 500, 600, 700 (default)"
            style={INPUT}
          />
          <div style={hintStyle}>
            Comma list of weights requested from Google Fonts for the families above.
            Blank = 400, 500, 600, 700.
          </div>
        </div>
        <div style={ROW}>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Base size (px)</label>
            <input
              type="number" min={12} max={24}
              value={resolved.typography.baseSize}
              onChange={(e) => patchTypography({ baseSize: parseInt(e.target.value, 10) || 16 })}
              style={INPUT}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Scale ratio</label>
            {/* Free number + named presets as suggestions: saved pages carry
                ratios like 1.38 / 1.45 that no fixed select could show. */}
            <input
              type="number" step={0.005} min={1} max={2}
              list="pg-theme-scale-ratios"
              value={rawType.scaleRatio ?? ''}
              placeholder={String(resolved.typography.scaleRatio)}
              onChange={(e) => patchTypography({ scaleRatio: numOrUndef(e.target.value) })}
              title={scaleRatioName(resolved.typography.scaleRatio)}
              style={INPUT}
            />
            <datalist id="pg-theme-scale-ratios">
              {SCALE_RATIOS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </datalist>
          </div>
        </div>
        <div style={ROW}>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Line height</label>
            <input
              type="number" step={0.05} min={1} max={2.5}
              value={resolved.typography.lineHeight}
              onChange={(e) => patchTypography({ lineHeight: parseFloat(e.target.value) || 1.6 })}
              style={INPUT}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Body weight</label>
            <select
              value={String(resolved.typography.fontWeight)}
              onChange={(e) => patchTypography({ fontWeight: parseInt(e.target.value, 10) })}
              style={INPUT}
            >
              <option value="300">Light · 300</option>
              <option value="400">Regular · 400</option>
              <option value="500">Medium · 500</option>
              <option value="600">Semibold · 600</option>
              <option value="700">Bold · 700</option>
            </select>
          </div>
        </div>

        {/* ── Heading detail — drives --pg-h1/2/3 + heading weight/line-height ── */}
        <div style={ROW}>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Heading weight</label>
            <select
              value={String(resolved.typography.headingWeight)}
              onChange={(e) => patchTypography({ headingWeight: parseInt(e.target.value, 10) })}
              style={INPUT}
            >
              <option value="500">Medium · 500</option>
              <option value="600">Semibold · 600</option>
              <option value="700">Bold · 700</option>
              <option value="800">Extrabold · 800</option>
              <option value="900">Black · 900</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Heading line height</label>
            <input
              type="number" step={0.05} min={0.9} max={1.8}
              value={resolved.typography.headingLineHeight}
              onChange={(e) => patchTypography({ headingLineHeight: parseFloat(e.target.value) || 1.2 })}
              style={INPUT}
            />
          </div>
        </div>
        <div style={ROW}>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Heading tracking (em)</label>
            <input
              type="number" step={0.005} min={-0.05} max={0.1}
              value={resolved.typography.headingLetterSpacing}
              onChange={(e) => patchTypography({ headingLetterSpacing: parseFloat(e.target.value) || 0 })}
              style={INPUT}
            />
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8625rem', cursor: 'pointer', paddingBottom: 6 }}>
              <input
                type="checkbox"
                checked={!!resolved.typography.fluid}
                onChange={(e) => patchTypography({ fluid: e.target.checked })}
              />
              Fluid headings (scale with screen)
            </label>
          </div>
        </div>

        {/* ── Eyebrow / overline — drives --pg-eyebrow-* (the small label
            above hero and section headings). Blank = runtime default. ── */}
        <div>
          <label style={miniLabel}>Eyebrow (overline label)</label>
          <div style={ROW}>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Size (px)</label>
              <input
                type="number" min={8} max={32}
                value={rawType.eyebrowSize ?? ''}
                placeholder={String(resolved.typography.eyebrowSize)}
                onChange={(e) => patchTypography({ eyebrowSize: numOrUndef(e.target.value) })}
                style={INPUT}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Weight</label>
              <OptionSelect
                value={rawType.eyebrowWeight != null ? String(rawType.eyebrowWeight) : undefined}
                options={WEIGHT_OPTIONS}
                unsetLabel={`Default · ${resolved.typography.eyebrowWeight}`}
                onChange={(v) => patchTypography({ eyebrowWeight: v === undefined ? undefined : numOrUndef(v) })}
                style={INPUT}
              />
            </div>
          </div>
          <div style={{ ...ROW, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Tracking (em)</label>
              <input
                type="number" step={0.01} min={-0.05} max={0.5}
                value={rawType.eyebrowTracking ?? ''}
                placeholder={String(resolved.typography.eyebrowTracking)}
                onChange={(e) => patchTypography({ eyebrowTracking: numOrUndef(e.target.value) })}
                style={INPUT}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Case</label>
              <OptionSelect
                value={rawType.eyebrowTransform}
                options={[
                  { value: 'uppercase', label: 'UPPERCASE' },
                  { value: 'none', label: 'As typed' },
                ]}
                unsetLabel={`Default · ${resolved.typography.eyebrowTransform === 'none' ? 'As typed' : 'UPPERCASE'}`}
                onChange={(v) => patchTypography({ eyebrowTransform: v as TypographyPreset['eyebrowTransform'] })}
                style={INPUT}
              />
            </div>
          </div>
        </div>

        <div>
          <label style={miniLabel}>Body measure (px)</label>
          <input
            type="number" min={0} max={2000} step={10}
            value={rawType.bodyMaxWidth ?? ''}
            placeholder={resolved.typography.bodyMaxWidth ? String(resolved.typography.bodyMaxWidth) : 'None (full width)'}
            onChange={(e) => patchTypography({ bodyMaxWidth: numOrUndef(e.target.value) })}
            style={INPUT}
          />
          <div style={hintStyle}>
            Maximum line length for paragraph text. ~640–720px reads comfortably. Blank or 0 = no limit.
          </div>
        </div>
      </section>

      {/* ── Layout ── */}
      <section style={SECTION}>
        <div style={FIELD_LABEL}>Layout</div>
        {/* An explicit Max width wins over the Content width preset
            (contentWidthPx in theme.ts), so the preset is dimmed and
            labelled as overridden whenever one is set. */}
        <div style={ROW}>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Content width</label>
            <OptionSelect
              value={resolved.layout.contentWidth}
              options={[
                { value: 'narrow', label: `Narrow · ${CONTENT_WIDTH_PX.narrow}px` },
                { value: 'normal', label: `Normal · ${CONTENT_WIDTH_PX.normal}px` },
                { value: 'wide', label: `Wide · ${CONTENT_WIDTH_PX.wide}px` },
                { value: 'full', label: `Full · ${CONTENT_WIDTH_PX.full}px` },
              ]}
              onChange={(v) => patchLayout({ contentWidth: v as ContentWidthPreset | undefined })}
              title={rawLayout.maxWidth != null ? `Overridden by Max width (${rawLayout.maxWidth}px)` : undefined}
              style={{ ...INPUT, opacity: rawLayout.maxWidth != null ? 0.55 : 1 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Max width (px)</label>
            <input
              type="number" min={320} max={3840} step={8}
              value={rawLayout.maxWidth ?? ''}
              placeholder={`${CONTENT_WIDTH_PX[resolved.layout.contentWidth] ?? ''} (from preset)`}
              onChange={(e) => patchLayout({ maxWidth: numOrUndef(e.target.value) })}
              style={INPUT}
            />
          </div>
        </div>
        {rawLayout.maxWidth != null && (
          <div style={{ ...hintStyle, marginTop: -4 }}>
            Max width {rawLayout.maxWidth}px overrides the Content width preset. Clear it to use the preset.
          </div>
        )}
        <div>
          <label style={miniLabel}>Default alignment</label>
          <OptionSelect
            value={rawLayout.align}
            options={[
              { value: 'left', label: 'Left' },
              { value: 'center', label: 'Center' },
              { value: 'right', label: 'Right' },
            ]}
            unsetLabel={`Default · ${resolved.layout.align}`}
            onChange={(v) => patchLayout({ align: v as Align | undefined })}
            style={INPUT}
          />
          <div style={hintStyle}>
            Published as the --pg-align token. Blocks with their own alignment keep it.
          </div>
        </div>
        <div style={ROW}>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Padding X (px)</label>
            <input
              type="number" min={0} max={200}
              value={resolved.layout.paddingX}
              onChange={(e) => patchLayout({ paddingX: parseInt(e.target.value, 10) || 0 })}
              style={INPUT}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Padding Y (px)</label>
            <input
              type="number" min={0} max={200}
              value={resolved.layout.paddingY}
              onChange={(e) => patchLayout({ paddingY: parseInt(e.target.value, 10) || 0 })}
              style={INPUT}
            />
          </div>
        </div>
        <div style={ROW}>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Block gap (px)</label>
            <input
              type="number" min={0} max={80}
              value={resolved.layout.blockGap}
              onChange={(e) => patchLayout({ blockGap: parseInt(e.target.value, 10) || 0 })}
              style={INPUT}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={miniLabel}>Radius (px)</label>
            <input
              type="number" min={0} max={40}
              value={resolved.layout.borderRadius}
              onChange={(e) => patchLayout({ borderRadius: parseInt(e.target.value, 10) || 0 })}
              style={INPUT}
            />
          </div>
        </div>

        {/* Page-wide background */}
        <div>
          <label style={miniLabel}>Page background</label>
          <BackgroundEditor
            background={resolved.layout.background}
            project={project}
            onChange={(bg) => patchLayout({ background: bg })}
          />
        </div>
      </section>

      {/* ── Dark mode ──
          A second, SPARSE palette re-declared under prefers-color-scheme
          ('auto') and/or a visitor toggle ('toggle') — see darkModeCss /
          resolveDarkPalette in theme.ts. Anything left blank is derived
          from the light palette, shown as each row's placeholder. */}
      <section style={SECTION}>
        <div style={FIELD_LABEL}>Dark mode</div>
        <div>
          <label style={miniLabel}>Mode</label>
          <OptionSelect
            value={darkMode.mode}
            options={[
              { value: 'off', label: 'Off — single palette' },
              { value: 'auto', label: 'Auto — follow the visitor\'s system setting' },
              { value: 'toggle', label: 'Toggle — follow the system, with a switch' },
            ]}
            unsetLabel="Off (default)"
            onChange={(v) => patchDarkMode({ mode: v as DarkModeConfig['mode'] })}
            style={INPUT}
          />
        </div>
        {darkMode.mode === 'toggle' && (
          <div style={ROW}>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Switch position</label>
              <OptionSelect
                value={darkMode.togglePosition}
                options={[
                  { value: 'floating', label: 'Floating button' },
                  { value: 'header', label: 'Header (no floating button)' },
                  { value: 'none', label: 'None (system only)' },
                ]}
                unsetLabel="Default · floating"
                onChange={(v) => patchDarkMode({ togglePosition: v as DarkModeConfig['togglePosition'] })}
                style={INPUT}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={miniLabel}>Storage key</label>
              <DraftInput
                value={darkMode.storageKey ?? ''}
                onCommit={(raw) => patchDarkMode({ storageKey: textOrUndef(raw) })}
                placeholder="pg-theme"
                style={INPUT}
              />
            </div>
          </div>
        )}
        {darkMode.mode && darkMode.mode !== 'off' ? (
          <PaletteGroup label={`Dark palette (${Object.values(darkMode.palette || {}).filter(Boolean).length} set)`}>
            <div style={hintStyle}>
              Only set what you want to pin — the rest is derived from the light palette with
              contrast checks. Clear a field to go back to the derived color.
            </div>
            {DARK_PALETTE_KEYS.map(([key, label]) => (
              <ColorRow
                key={key}
                label={label}
                value={darkMode.palette?.[key] ?? ''}
                placeholder={darkDerived?.[key]}
                onChange={(c) => patchDarkPalette({ [key]: c || undefined })}
              />
            ))}
          </PaletteGroup>
        ) : (
          darkMode.palette && Object.keys(darkMode.palette).length > 0 && (
            <div style={hintStyle}>
              A dark palette is saved but not in use while dark mode is off.
            </div>
          )
        )}
      </section>

      {/* ── Custom CSS escape hatch removed — keep theme persisted as data only */}
    </div>
  )
}

const miniLabel: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  display: 'block',
}

const hintStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
  marginTop: 4,
}

// Named modular-scale ratios offered as suggestions for the free-number
// Scale ratio input (any ratio is valid; these are the classic steps).
const SCALE_RATIOS: { value: number; label: string }[] = [
  { value: 1.125, label: 'Minor 2nd' },
  { value: 1.2, label: 'Minor 3rd' },
  { value: 1.25, label: 'Major 3rd' },
  { value: 1.333, label: 'Perfect 4th' },
  { value: 1.414, label: 'Aug 4th' },
  { value: 1.5, label: 'Perfect 5th' },
  { value: 1.618, label: 'Golden' },
]

function scaleRatioName(r: number): string {
  const hit = SCALE_RATIOS.find(x => Math.abs(x.value - r) < 0.0005)
  return hit ? `${hit.label} · ${hit.value}` : `Custom ratio · ${r}`
}

const WEIGHT_OPTIONS = [
  { value: '300', label: 'Light · 300' },
  { value: '400', label: 'Regular · 400' },
  { value: '500', label: 'Medium · 500' },
  { value: '600', label: 'Semibold · 600' },
  { value: '700', label: 'Bold · 700' },
  { value: '800', label: 'Extrabold · 800' },
  { value: '900', label: 'Black · 900' },
]

// Every PalettePreset token, in the editor's group order, for the sparse
// dark palette. `textSecondary` (legacy alias of `muted`) is deliberately
// left out — writing it would shadow `muted`.
const DARK_PALETTE_KEYS: [keyof PalettePreset, string][] = [
  ['background', 'Background'], ['surface', 'Surface'], ['surfaceAlt', 'Surface alt'],
  ['surfaceMuted', 'Surface muted'], ['elevated', 'Elevated'],
  ['border', 'Border'], ['borderStrong', 'Border strong'],
  ['heading', 'Heading'], ['text', 'Body'], ['muted', 'Muted'], ['subtle', 'Subtle'],
  ['link', 'Link'], ['linkHover', 'Link hover'], ['inverted', 'Inverted'],
  ['onSurface', 'On surface'], ['onSurfaceAlt', 'On surface alt'],
  ['primary', 'Primary'], ['primaryHover', 'Primary hover'], ['onPrimary', 'On primary'],
  ['accent', 'Accent'], ['accentHover', 'Accent hover'], ['onAccent', 'On accent'],
  ['info', 'Info'], ['infoBg', 'Info bg'], ['success', 'Success'], ['successBg', 'Success bg'],
  ['warning', 'Warning'], ['warningBg', 'Warning bg'], ['error', 'Error'], ['errorBg', 'Error bg'],
]

// `smartMergePreset` rebuilds the theme from palette/typography/layout only,
// so it would silently switch dark mode off on a preset change. Dark mode is
// a page setting, not part of a preset's look — carry it across.
function withDarkMode(next: PageTheme, prev: PageTheme | undefined): PageTheme {
  return prev?.darkMode ? { ...next, darkMode: prev.darkMode } : next
}

// True when the page's saved theme has *any* override beyond a preset
// id — i.e. there are palette/typography/layout fields the user (or AI)
// has explicitly set. Used to decide whether to skip the switch-preset
// confirmation (no customizations = nothing to lose) and whether to
// show the "Reset to preset defaults" affordance.
function hasCustomizations(theme: import('./types').PageTheme | undefined): boolean {
  if (!theme) return false
  const hasFields = (o: object | undefined) => !!o && Object.keys(o).length > 0
  return hasFields(theme.palette) || hasFields(theme.typography) || hasFields(theme.layout)
    || hasFields(theme.darkMode)
}

// ─── Preset switch / reset confirmation modal ───────────
// Centered modal overlay (uses the shared Modal component) so the
// warning takes the user's full attention before overwriting their
// theme. Two primary actions: "Keep customizations" runs the smart
// merge, "Replace all" drops every override and re-applies the
// preset's pristine values. `isReset` flips the copy when the user
// explicitly asked to reset to the *currently active* preset.
function PresetSwitchModal({
  preset, isReset, onKeep, onReplace, onCancel,
}: {
  preset: import('./theme').ThemePreset | null
  isReset: boolean
  onKeep: () => void
  onReplace: () => void
  onCancel: () => void
}) {
  const open = !!preset
  // Render the body even when closed so React keeps the component
  // mounted; Modal hides itself when `open` is false. Bail when there
  // is no preset to avoid undefined access during the closing tick.
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={preset ? (isReset ? `Reset to ${preset.label} defaults?` : `Apply ${preset.label}?`) : ''}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" onClick={onCancel} style={modalBtnStyle('secondary')}>Cancel</button>
          {!isReset && (
            <button type="button" onClick={onKeep} style={modalBtnStyle('secondary')}>Keep customizations</button>
          )}
          <button type="button" onClick={onReplace} style={modalBtnStyle('danger')}>Replace all</button>
        </div>
      }
    >
      {preset && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: '0.9625rem', color: 'var(--color-text)', lineHeight: 1.5 }}>
            {isReset
              ? 'This will drop every color, font, and layout customization on this page and re-apply the preset\'s defaults.'
              : 'Choose how to apply this preset to your page:'}
          </p>
          {!isReset && (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.9125rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              <li><strong style={{ color: 'var(--color-text)' }}>Keep my customizations</strong> — only fields you didn't change move to the new preset.</li>
              <li><strong style={{ color: 'var(--color-text)' }}>Replace all</strong> — drops every override and applies the full preset look.</li>
            </ul>
          )}
          <div style={{
            padding: '10px 12px', borderRadius: 6,
            background: 'var(--pg-warning-bg, rgba(234,179,8,0.12))',
            color: 'var(--pg-warning, #b45309)',
            fontSize: '0.8625rem', lineHeight: 1.5,
            display: 'flex', gap: 8, alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: '1.0625rem', lineHeight: 1 }}>⚠</span>
            <span>
              <strong>Replace all</strong> overwrites every color, font, and layout setting on this
              page. Use the toolbar's undo if you change your mind.
            </span>
          </div>
        </div>
      )}
    </Modal>
  )
}

function modalBtnStyle(kind: 'primary' | 'secondary' | 'danger'): React.CSSProperties {
  // `danger` styles the Replace-all button with the theme's warning
  // color so it visually carries the destructive weight. `primary`
  // (currently unused) is kept for callers that want the standard
  // accent-colored confirm button.
  if (kind === 'danger') {
    return {
      padding: '8px 16px', fontSize: '0.9125rem', fontWeight: 600,
      borderRadius: 6, cursor: 'pointer',
      border: '1px solid var(--pg-warning, #b45309)',
      background: 'var(--pg-warning, #b45309)', color: '#fff',
    }
  }
  if (kind === 'primary') {
    return {
      padding: '8px 16px', fontSize: '0.9125rem', fontWeight: 600,
      borderRadius: 6, cursor: 'pointer',
      border: '1px solid var(--color-accent)',
      background: 'var(--color-accent)', color: '#fff',
    }
  }
  return {
    padding: '8px 16px', fontSize: '0.9125rem', fontWeight: 600,
    borderRadius: 6, cursor: 'pointer',
    border: '1px solid var(--color-border)',
    background: 'transparent', color: 'var(--color-text)',
  }
}

// ─── Palette group — collapsible token bucket ────────────
// `details/summary` so we don't introduce a new state mechanism just for
// expand/collapse. The summary is styled to match the existing field
// label so the editor reads consistently.
function PaletteGroup({
  label, defaultOpen, children,
}: {
  label: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  return (
    <details
      open={defaultOpen}
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        padding: '6px 10px 8px',
        background: 'var(--color-bg)',
      }}
    >
      <summary
        style={{
          listStyle: 'none', cursor: 'pointer',
          fontSize: '0.8125rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.04em',
          color: 'var(--color-text-secondary)',
          padding: '2px 0',
          userSelect: 'none',
        }}
      >
        {label}
      </summary>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8 }}>
        {children}
      </div>
    </details>
  )
}

// ─── Live preview of resolved tokens ─────────────────────
// Renders a compact card that shows the active palette in context: a
// heading, body text, muted caption, primary button, link, and one of
// each semantic callout. Lets the author verify all derived/explicit
// tokens read cleanly together without leaving the property panel.
function ThemePreview({ palette }: { palette: import('./types').PalettePreset }) {
  const p = palette as Required<import('./types').PalettePreset>
  return (
    <div
      style={{
        marginTop: 8,
        borderRadius: 8,
        background: p.background,
        border: `1px solid ${p.border}`,
        padding: 14,
        display: 'flex', flexDirection: 'column', gap: 10,
        // Pin the body font to system here; the surrounding canvas is
        // where the chosen font family applies. The preview is about
        // colors, not type.
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: p.heading, lineHeight: 1.2 }}>Theme preview</div>
      <div style={{ fontSize: 13, color: p.text, lineHeight: 1.5 }}>
        Body text reads against the background.{' '}
        <a style={{ color: p.link, textDecoration: 'underline' }} onClick={(e) => e.preventDefault()} href="#">
          link
        </a>
      </div>
      <div style={{ fontSize: 13, color: p.muted }}>Muted caption — secondary information</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span style={{
          padding: '4px 10px', fontSize: 13, fontWeight: 600, borderRadius: 6,
          background: p.primary, color: p.onPrimary,
        }}>Primary</span>
        <span style={{
          padding: '4px 10px', fontSize: 13, fontWeight: 600, borderRadius: 6,
          background: p.surface, color: p.onSurface ?? p.text, border: `1px solid ${p.border}`,
        }}>Secondary</span>
        <span style={{
          padding: '4px 10px', fontSize: 13, fontWeight: 600, borderRadius: 6,
          background: p.accent, color: p.onAccent ?? '#fff',
        }}>Accent</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {([
          ['info', p.info, p.infoBg, 'Info note'],
          ['success', p.success, p.successBg, 'Success'],
          ['warning', p.warning, p.warningBg, 'Warning'],
          ['error', p.error, p.errorBg, 'Error'],
        ] as const).map(([key, color, bg, label]) => (
          <div key={key} style={{
            background: bg, color: p.text, padding: '4px 8px', fontSize: 13,
            borderLeft: `3px solid ${color}`, borderRadius: 4,
          }}>{label}</div>
        ))}
      </div>
    </div>
  )
}

// ─── Color row with swatch + hex input ───────────────────

function ColorRow({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  // Shown when `value` is '' (an unset, derived token); the swatch then
  // previews the derived color too.
  placeholder?: string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <label style={{ fontSize: '0.8425rem', width: 100, color: 'var(--color-text-secondary)' }}>
        {label}
      </label>
      <input
        type="color"
        value={normalizeColor(value || placeholder || '')}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 36, height: 28, padding: 0, border: '1px solid var(--color-border)',
          borderRadius: 6, background: 'transparent', cursor: 'pointer',
        }}
      />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...INPUT, fontFamily: 'monospace', flex: 1 }}
      />
    </div>
  )
}

function normalizeColor(c: string): string {
  // <input type=color> only accepts #rrggbb. Fallback for var()/rgba/etc.
  if (/^#[0-9a-fA-F]{6}$/.test(c)) return c
  return '#000000'
}

// ─── Font picker (swatch shows family) ───────────────────

function FontPicker({
  value,
  onChange,
  overriddenBy,
}: {
  value: FontFamilyPreset
  onChange: (v: FontFamilyPreset) => void
  // A custom Google family that the runtime renders INSTEAD of this preset;
  // the preset is then only the fallback stack, and the button says so.
  overriddenBy?: string
}) {
  const [open, setOpen] = useState(false)
  const families = Object.keys(FONT_STACKS) as FontFamilyPreset[]
  const custom = overriddenBy?.trim()
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
        title={custom ? `Rendered as "${custom}"; ${value} is only the fallback stack` : undefined}
        style={{
          ...INPUT, textAlign: 'left', cursor: 'pointer',
          fontFamily: custom ? `"${custom}", ${FONT_STACKS[value]}` : FONT_STACKS[value],
        }}
      >
        {custom
          ? <>{custom} <span style={{ opacity: 0.6 }}>(custom · fallback {value})</span></>
          : <>{value} — The quick brown fox</>}
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
            marginTop: 4, padding: 4,
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)', borderRadius: 6,
            maxHeight: 240, overflow: 'auto',
            boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
          }}
        >
          {families.map((f) => (
            <div
              key={f}
              onClick={() => { onChange(f); setOpen(false) }}
              style={{
                padding: '6px 10px', borderRadius: 4, cursor: 'pointer',
                fontFamily: FONT_STACKS[f],
                background: f === value ? 'var(--color-accent-weak)' : 'transparent',
              }}
            >
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{f}</div>
              <div style={{ fontSize: '1.0125rem' }}>The quick brown fox jumps</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Custom Google family override ───────────────────────
// Draft text committed on blur/Enter (a half-typed family name would
// otherwise be requested from Google Fonts on every keystroke). Blank
// clears the override so the preset family applies again.

function CustomFamilyInput({
  label, value, presetFamily, onCommit,
}: {
  label: string
  value: string | undefined
  presetFamily: string | undefined
  onCommit: (v: string | undefined) => void
}) {
  const active = !!value?.trim()
  return (
    <div style={{ marginTop: 6 }}>
      <label style={miniLabel}>{label}</label>
      <div style={ROW}>
        <DraftInput
          value={value ?? ''}
          onCommit={(raw) => onCommit(textOrUndef(raw))}
          placeholder={presetFamily ? `e.g. Space Grotesk (blank = ${presetFamily})` : 'e.g. Space Grotesk (blank = preset)'}
          style={{ ...INPUT, flex: 1, borderColor: active ? 'var(--color-accent)' : 'var(--color-border)' }}
        />
        {active && (
          <button
            type="button"
            onClick={() => onCommit(undefined)}
            title="Clear the custom family and use the preset font"
            style={{
              padding: '5px 8px', fontSize: '0.8125rem', cursor: 'pointer',
              border: '1px solid var(--color-border)', borderRadius: 6,
              background: 'transparent', color: 'var(--color-text-secondary)',
            }}
          >
            Use preset
          </button>
        )}
      </div>
      {active && (
        <div style={hintStyle}>
          Overrides the preset above — visitors see <strong>{value}</strong>; the preset is only the fallback.
        </div>
      )}
    </div>
  )
}

// ─── Background editor (color / gradient / image / none) ──

function BackgroundEditor({
  background,
  project,
  onChange,
}: {
  background: Background
  project: string
  onChange: (bg: Background) => void
}) {
  const kind = background.kind || 'none'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <select
        value={kind}
        onChange={(e) => {
          const k = e.target.value as Background['kind']
          if (k === 'none') onChange({ kind: 'none' })
          else if (k === 'color') onChange({ kind: 'color', color: background.color || '#ffffff' })
          else if (k === 'gradient') onChange({
            kind: 'gradient',
            gradientFrom: background.gradientFrom || '#0f172a',
            gradientTo: background.gradientTo || '#1e3a8a',
            gradientAngle: background.gradientAngle ?? 135,
          })
          else if (k === 'image') onChange({ kind: 'image', imageUrl: background.imageUrl || '' })
        }}
        style={INPUT}
      >
        <option value="none">None</option>
        <option value="color">Solid color</option>
        <option value="gradient">Gradient</option>
        <option value="image">Image URL</option>
      </select>

      {kind === 'color' && (
        <ColorRow
          label="Color"
          value={background.color || '#ffffff'}
          onChange={(c) => onChange({ ...background, kind: 'color', color: c })}
        />
      )}
      {kind === 'gradient' && (
        <>
          <ColorRow
            label="From"
            value={background.gradientFrom || '#0f172a'}
            onChange={(c) => onChange({ ...background, kind: 'gradient', gradientFrom: c })}
          />
          <ColorRow
            label="To"
            value={background.gradientTo || '#1e3a8a'}
            onChange={(c) => onChange({ ...background, kind: 'gradient', gradientTo: c })}
          />
          <div style={ROW}>
            <label style={{ fontSize: '0.8425rem', width: 100, color: 'var(--color-text-secondary)' }}>
              Angle (°)
            </label>
            <input
              type="number" min={0} max={360}
              value={background.gradientAngle ?? 135}
              onChange={(e) => onChange({
                ...background, kind: 'gradient',
                gradientAngle: parseInt(e.target.value, 10) || 0,
              })}
              style={INPUT}
            />
          </div>
        </>
      )}
      {kind === 'image' && (
        <>
          <AssetPickerField
            project={project}
            value={background.imageUrl}
            accept="image/*"
            onChange={(url) => onChange({ ...background, kind: 'image', imageUrl: url })}
          />
          <input
            type="text"
            placeholder="overlay e.g. rgba(0,0,0,0.4) (optional)"
            value={background.overlay || ''}
            onChange={(e) => onChange({ ...background, kind: 'image', overlay: e.target.value })}
            style={INPUT}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8425rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!background.parallax}
              onChange={(e) => onChange({ ...background, parallax: e.target.checked })}
            />
            Parallax on scroll
          </label>
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: -2 }}>
            Background stays fixed while the page scrolls over it (desktop; static on mobile).
          </span>
        </>
      )}
    </div>
  )
}
